/**
 * useAuth — single source of truth for the current user.
 *
 * Bootstrap is memoized in api-client (`bootstrapAuth`) so multiple consumers
 * share one /auth/refresh attempt. Once the access token store has a token,
 * /auth/me populates the cached `user`.
 */
import type {
  LoginOrMfaResponse,
  LoginResponse,
  MeResponse,
  UserSummary,
} from '@peerless/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';

import { apiRequest, authStore, bootstrapAuth } from '../lib/api-client';
import { queryKeys } from '../lib/query-keys';


export type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

/** Login result is either a fully-authenticated user or an MFA-pending state. */
export type LoginResult =
  | { kind: 'authenticated'; user: UserSummary }
  | { kind: 'mfa_required'; challengeToken: string; challengeExpiresAt: number };

export interface UseAuthResult {
  user: UserSummary | null;
  status: AuthStatus;
  login: (email: string, password: string) => Promise<LoginResult>;
  verifyMfa: (challengeToken: string, code: string) => Promise<UserSummary>;
  logout: () => Promise<void>;
}

export function useAuth(): UseAuthResult {
  const queryClient = useQueryClient();
  const [bootstrapped, setBootstrapped] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void bootstrapAuth().finally(() => {
      if (!cancelled) setBootstrapped(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const meQuery = useQuery({
    queryKey: queryKeys.auth.me(),
    queryFn: () => apiRequest<MeResponse>('/auth/me'),
    enabled: bootstrapped && authStore.get().accessToken !== null,
    retry: false,
    staleTime: 60_000,
  });

  const login = useCallback(
    async (email: string, password: string): Promise<LoginResult> => {
      const res = await apiRequest<LoginOrMfaResponse>('/auth/login', {
        method: 'POST',
        body: { email, password },
        unauthenticated: true,
      });
      if ('mfaRequired' in res) {
        return { kind: 'mfa_required', challengeToken: res.challengeToken, challengeExpiresAt: res.challengeExpiresAt };
      }
      authStore.set({
        accessToken: res.accessToken,
        accessTokenExpiresAt: res.accessTokenExpiresAt,
      });
      queryClient.setQueryData(queryKeys.auth.me(), { user: res.user });
      return { kind: 'authenticated', user: res.user };
    },
    [queryClient],
  );

  const verifyMfa = useCallback(
    async (challengeToken: string, code: string): Promise<UserSummary> => {
      const res = await apiRequest<LoginResponse>('/auth/mfa/verify', {
        method: 'POST',
        body: { challengeToken, code },
        unauthenticated: true,
      });
      authStore.set({
        accessToken: res.accessToken,
        accessTokenExpiresAt: res.accessTokenExpiresAt,
      });
      queryClient.setQueryData(queryKeys.auth.me(), { user: res.user });
      return res.user;
    },
    [queryClient],
  );

  const logout = useCallback(async (): Promise<void> => {
    try {
      await apiRequest<{ ok: boolean }>('/auth/logout', { method: 'POST' });
    } catch {
      // ignore — clear local state regardless
    }
    authStore.clear();
    // Use setQueryData (not removeQueries) so subscribers re-render
    // immediately. removeQueries evicts the cache entry but doesn't always
    // trigger a re-render of consumers — meaning RequireAuth keeps seeing
    // the cached user and doesn't redirect until the next navigation.
    queryClient.setQueryData<MeResponse | null>(queryKeys.auth.me(), null);
    // Cancel any in-flight queries that would otherwise resolve with stale
    // data after the user-data has been cleared.
    await queryClient.cancelQueries();
    // Wipe the entire react-query cache so per-user data (sheets, agents,
    // conversations, etc.) doesn't bleed into the next session.
    queryClient.clear();
  }, [queryClient]);

  let status: AuthStatus;
  if (!bootstrapped || (authStore.get().accessToken !== null && meQuery.isLoading)) {
    status = 'loading';
  } else if (meQuery.data?.user) {
    status = 'authenticated';
  } else {
    status = 'unauthenticated';
  }

  return {
    user: meQuery.data?.user ?? null,
    status,
    login,
    verifyMfa,
    logout,
  };
}
